<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, reactive, ref, watch } from "vue"
import { useMachine } from "@xstate/vue"
import { createFormPceMachine } from "@core/components/source/meter/form/pce/machine/pce-form.machine"
import type {
    PceFormSearchExistPayload,
    PceFormSearchRestore,
} from "@core/components/source/meter/form/pce/PceFormSearch.vue"
import type { SourceConsentSignBuilding } from "@core/types/source/consent_request"
import type { OwnerOutput } from "@core/composables"
import type { Address } from "@types"
import type { SourceAlreadyExistDonePayload } from "@core/components/source/meter/form/invitation/error/SourceAlreadyExist.vue"
import type { SourceAppendDonePayload } from "@core/components/source/form/append/source-append.types"

// ================================
// TYPES & INTERFACES
// ================================
export interface PceFormProps {
    buildingId?: string
    /** Adresse du bâtiment pour la recherche PCE (optionnel) */
    address?: Address | null
    /** Affiche le Retour sur l’étape titulaire (modale d’ajout de source) */
    showOwnerStepBack?: boolean
    /** Entrée directe sans sélecteur de type : Retour titulaire / shell masqués */
    appendSkippedTypeSelector?: boolean
    /**
     * Si true : PUT sign avec `disable_task: true` (ex. onboarding bâtiment où la tâche est lancée manuellement après).
     * @default false
     */
    consentSignDisableTask?: boolean
}

type AppendShellBackPayload = { hidden: boolean; disabled: boolean }

interface Emits {
    (e: "back"): void
    (e: "done", _id_source?: string, source?: SourceAppendDonePayload): void
    (e: "update:shellBack", payload: AppendShellBackPayload): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<PceFormProps>(), {
    buildingId: undefined,
    address: undefined,
    showOwnerStepBack: false,
    appendSkippedTypeSelector: false,
    consentSignDisableTask: false,
})
const emit = defineEmits<Emits>()

// ================================
// MACHINE
// ================================
const { snapshot, send } = useMachine(createFormPceMachine())

const stateName = computed(() => {
    const v = snapshot.value.value
    return typeof v === "string" ? v : ""
})

/** Bâtiment pour SourceConsentSign (étape signature consentement PCE) */
const sourceConsentSignBuilding = computed<SourceConsentSignBuilding>(() => ({
    _id_building: props.buildingId ?? "",
    address: (props.address ?? {}) as SourceConsentSignBuilding["address"],
}))

/** Props SourceConsentSign dérivées du contexte (aligné PdlForm) */
const consentSignOwner = computed(() => snapshot.value.context.owner)
const consentSignSource = computed(() => ({
    idsource: snapshot.value.context.pce ?? "",
    idtype: "pce" as const,
    date_from_data: snapshot.value.context.date_from_data,
}))
const consentSignRequest = computed(() => ({
    type: "SIGN" as const,
    mail: true,
    duration: 60,
    disable_task: props.consentSignDisableTask,
}))

const isStorybook = import.meta.env.STORYBOOK === "true"
const machineStateForStorybook = isStorybook
    ? reactive<{ value: unknown; context: unknown }>({
          value: undefined,
          context: undefined,
      })
    : undefined

if (isStorybook && machineStateForStorybook) {
    watch(
        snapshot,
        (s) => {
            machineStateForStorybook.value = s.value
            machineStateForStorybook.context = s.context
        },
        { immediate: true }
    )
}

defineExpose(
    isStorybook && machineStateForStorybook
        ? { machineState: machineStateForStorybook }
        : {}
)

/** Réhydrate PceFormSearch (PCE + date + payload en attente) après BACK depuis une étape aval */
const pceSearchRestore = computed((): PceFormSearchRestore | undefined => {
    const c = snapshot.value.context
    if (!c.pce) return undefined

    const hasDate = Boolean(c.date_from_data)
    const canRebuildPending =
        typeof c.pceSearchExists === "boolean" && c.dda != null

    if (hasDate && canRebuildPending) {
        return {
            step: "awaiting_date",
            pce: c.pce,
            dateFromData: c.date_from_data!,
            pending: {
                exists: c.pceSearchExists!,
                type: "pce",
                pce: c.pce,
                dda: c.dda!,
                invitation: c.invitation,
                consentementUser: c.pceSearchConsentementUser,
                _id_source: c._id_source,
                sameTitulaireEmail: c.sameTitulaireEmail,
            },
        }
    }

    return {
        step: "verify",
        pce: c.pce,
    }
})

/** Au retour sur SourceFormOwner (ex. BACK depuis PceFormSearch), restaure le titulaire depuis le contexte pour réafficher "Mon entreprise" si besoin */
watch(stateName, (name, prevName) => {
    if (
        name === "SourceFormOwner" &&
        prevName !== "SourceFormOwner" &&
        snapshot.value.context.owner
    ) {
        ownerOutput.value = snapshot.value.context.owner
    }
})

// ================================
// INTERACTIONS
// ================================
function onBack(): void {
    send({ type: "BACK" })
    if (stateName.value === "idle") emit("back")
}

const ownerOutput = ref<OwnerOutput | null>(null)

function onOwnerSubmit(): void {
    const owner = ownerOutput.value
    if (!owner) return
    send({ type: "OWNER_SUBMIT", owner })
}

function onOwnerStepBack(): void {
    emit("back")
}

function onExist(payload: PceFormSearchExistPayload): void {
    send({
        type: "PCE_EXIST",
        exists: payload.exists,
        pce: payload.pce,
        invitation: payload.invitation,
        consentementUser: payload.consentementUser,
        _id_source: payload._id_source,
        dda: payload.dda,
        date_from_data: payload.date_from_data,
        sameTitulaireEmail: payload.sameTitulaireEmail,
    })
}

/** Payload émis par SourceConsentSign après signature (consentId, sourceId de l’API métier) */
function onConsentSigned(payload?: {
    consentId?: string
    sourceId?: string
    contractName?: string
    taskOnboarding?: unknown
}): void {
    send({
        type: "CONSENT_SIGNED",
        ...(payload && {
            _id_source: payload.sourceId,
            _id_consent: payload.consentId,
            contractName: payload.contractName,
            taskOnboarding: payload.taskOnboarding,
        }),
    })
}

function onGrdfDone(): void {
    const _id_source = snapshot.value.context._id_source
    send({ type: "GRDF_DONE" })
    if (!_id_source) {
        emit("done", _id_source)
        return
    }
    const source: SourceAppendDonePayload = {
        _id_source,
        idsource: snapshot.value.context.pce,
        type: "pce",
        idtype: "pce",
        operator: "grdf",
        isInvitationSent: false,
    }
    if (snapshot.value.context.taskOnboarding != null) {
        source.taskOnboarding = snapshot.value.context.taskOnboarding
    }
    emit("done", _id_source, source)
}

const isConsentSigning = ref(false)

function emitShellBackState(): void {
    const n = stateName.value
    emit("update:shellBack", {
        hidden:
            n === "PceFormGrdfDeclaration" || props.appendSkippedTypeSelector,
        disabled: n === "SourceConsentSign" && isConsentSigning.value,
    })
}

watch(stateName, emitShellBackState, { immediate: true })
watch(isConsentSigning, emitShellBackState)
watch(() => props.appendSkippedTypeSelector, emitShellBackState)

function onInvitationSent(payload: {
    _id_consentrequest: string
    url: string
    _id_source?: string
}): void {
    send({
        type: "INVITATION_SENT",
        _id_consentrequest: payload._id_consentrequest,
        url: payload.url,
        _id_source: payload._id_source,
    })
}

/** Payload renvoyé par SourceInvitationShare après création de la demande de partage */
function onAskSharingSent(payload: {
    _id?: string
    token?: string
    url?: string
}): void {
    const id = payload._id ?? ""
    const path = `/demande/partage-consentement/${id}`
    const link =
        payload.url?.trim() ||
        (typeof window !== "undefined"
            ? `${window.location.origin}${path}`
            : path)
    send({
        type: "ASK_SHARING_SENT",
        _id_consentrequest: id,
        url: link,
    })
}

/** Fermeture du formulaire depuis SourceInvitationSignSent (pas de retour en arrière). */
function onInvitationSentDone(): void {
    send({ type: "UPDATE_FORM_DONE" })
    const _id_source = snapshot.value.context._id_source
    if (!_id_source) {
        emit("done", _id_source)
        return
    }
    emit("done", _id_source, {
        _id_source,
        idsource: snapshot.value.context.pce,
        type: "pce",
        idtype: "pce",
        operator: "grdf",
        consentrequest: {
            _id: snapshot.value.context._id_consentrequest,
            state: "pending",
        },
        isInvitationSent: true,
    })
}

function onAlreadyExistDone(payload?: SourceAlreadyExistDonePayload): void {
    send({ type: "UPDATE_FORM_DONE" })
    emit("done", payload?._id_source)
}

function onExistErrorRetry(): void {
    send({ type: "RETRY" })
}

/** Fermeture du formulaire depuis SourceInvitationShareSent (bouton Terminer, comme SourceInvitationSignSent). */
function onAskSharingSentDone(): void {
    send({ type: "UPDATE_FORM_DONE" })
    const _id_source = snapshot.value.context._id_source
    if (!_id_source) {
        emit("done", _id_source)
        return
    }
    emit("done", _id_source, {
        _id_source,
        idsource: snapshot.value.context.pce,
        type: "pce",
        idtype: "pce",
        operator: "grdf",
        consentrequest: {
            _id: snapshot.value.context._id_consentrequest,
            state: "pending",
        },
        isInvitationSent: true,
    })
}

onMounted(() => {
    send({ type: "START" })
})
</script>

<template>
    <div class="source-append-pce flex flex-col gap-6 w-full">
        <!-- Titulaire (étape optionnelle invitation) -->
        <template v-if="stateName === 'SourceFormOwner'">
            <SourceFormOwner v-model="ownerOutput">
                <template #footer>
                    <div
                        class="flex w-full gap-3 flex-wrap"
                        :class="
                            showOwnerStepBack && !appendSkippedTypeSelector
                                ? 'justify-between'
                                : 'justify-end'
                        "
                    >
                        <BaseButton
                            v-if="
                                showOwnerStepBack && !appendSkippedTypeSelector
                            "
                            label="Retour"
                            severity="secondary"
                            class="min-w-[10rem]"
                            @click="onOwnerStepBack"
                        />
                        <BaseButton
                            label="Suivant"
                            class="min-w-[10rem]"
                            :disabled="!ownerOutput"
                            @click="onOwnerSubmit"
                        />
                    </div>
                </template>
            </SourceFormOwner>
        </template>

        <!-- Recherche PCE -->
        <PceFormSearch
            v-else-if="stateName === 'PceFormSearch'"
            :address="address ?? undefined"
            :owner="snapshot.context.owner"
            :invitation="snapshot.context.invitation"
            :restore="pceSearchRestore"
            @back="onBack"
            @exist="onExist"
        />

        <!-- Attente DDA -->
        <PceFormWaitingDda
            v-else-if="stateName === 'PceFormWaitingDda'"
            :idsource="snapshot.context.pce ?? ''"
            @back="onBack"
        />

        <!-- Signature consentement -->
        <SourceConsentSign
            v-else-if="
                stateName === 'SourceConsentSign' &&
                consentSignOwner &&
                consentSignSource.idsource
            "
            :owner="consentSignOwner"
            :source="consentSignSource"
            :building="sourceConsentSignBuilding"
            :request="consentSignRequest"
            @back="onBack"
            @signed="onConsentSigned"
            @update:signing="isConsentSigning = $event"
        />

        <!-- Déclaration GRDF -->
        <PceFormGrdfDeclaration
            v-else-if="stateName === 'PceFormGrdfDeclaration'"
            :meter-id="snapshot.context._id_source ?? ''"
            :id-consent="snapshot.context._id_consent ?? ''"
            @done="onGrdfDone"
        />

        <!-- Invitation -->
        <SourceInvitationSign
            v-else-if="stateName === 'SourceInvitationSign'"
            :building-id="props.buildingId ?? ''"
            :address="props.address ?? {}"
            :contract-name="snapshot.context.contractName ?? ''"
            :idsource="snapshot.context.pce ?? ''"
            :owner="
                snapshot.context.owner ?? {
                    user: { firstname: '', lastname: '', username: '' },
                    company: null,
                }
            "
            type="pce"
            :date="snapshot.context.date_from_data ?? ''"
            @back="onBack"
            @sent="onInvitationSent"
        />
        <SourceInvitationSignSent
            v-else-if="stateName === 'SourceInvitationSignSent'"
            :consent-request-id="snapshot.context._id_consentrequest ?? ''"
            :link="snapshot.context.url ?? ''"
            consent-request-type="SIGN"
            :multiple="false"
            :send-back="false"
            @done="onInvitationSentDone"
        />

        <!-- Demande de partage -->
        <SourceInvitationShare
            v-else-if="stateName === 'SourceInvitationShare'"
            :source-id="snapshot.context._id_source ?? ''"
            :idsource="snapshot.context.pce ?? ''"
            :building-id="buildingId ?? ''"
            :is-pce="true"
            @back="onBack"
            @sent="onAskSharingSent"
        />
        <SourceInvitationShareSent
            v-else-if="stateName === 'SourceInvitationShareSent'"
            :consent-request-id="snapshot.context._id_consentrequest ?? ''"
            :link="snapshot.context.url ?? ''"
            consent-request-type="SHARE"
            :multiple="false"
            :send-back="false"
            @done="onAskSharingSentDone"
        />

        <!-- Déjà existant -->
        <SourceAlreadyExist
            v-else-if="stateName === 'SourceAlreadyExist'"
            :source-id="snapshot.context._id_source ?? ''"
            :idsource="snapshot.context.pce ?? ''"
            :building-id="buildingId ?? ''"
            :is-pce="true"
            :from-building="false"
            @done="onAlreadyExistDone"
            @click:quit="onBack"
        />

        <!-- Erreur -->
        <SourceExistError
            v-else-if="stateName === 'SourceExistError'"
            :idsource="snapshot.context.pce ?? ''"
            :is-pce="true"
            @retry="onExistErrorRetry"
            @click:quit="onBack"
        />

        <!-- Bouton Retour si idle (ne devrait pas s'afficher en flux normal) -->
        <div
            v-else-if="stateName === 'idle' && !appendSkippedTypeSelector"
            class="flex gap-3"
        >
            <BaseButton label="Retour" severity="secondary" @click="onBack" />
        </div>
    </div>
</template>
